import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sidebar } from './Sidebar';
import { MobileNav } from './MobileNav';
import { ProfileButton, type AccountsState } from '../../accounts';
import { StorageWarningBanner } from '../shared/StorageWarningBanner';
import { FloatingDjay } from '../../mascot';
import { AchievementCeremony } from '../shared/AchievementCeremony';
import { SkinEvolutionReveal } from '../shared/SkinEvolutionReveal';
import { ConnectionStatus } from '../shared/ConnectionStatus';
import { InstallAppPrompt } from '../shared/InstallAppPrompt';
import { Menu } from 'lucide-react';
import { APP_NAME } from '../../config/constants';
import { useDirectionalTransition, getTransitionVariants } from '../../app/hooks/useDirectionalTransition';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
interface AppShellProps {
  children: React.ReactNode;
  currentView: string;
  onNavigate: (view: string) => void;
  accounts?: AccountsState;
  onLogout?: () => void;
}
export function AppShell({
  children,
  currentView,
  onNavigate,
  accounts,
  onLogout
}: AppShellProps) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  // Auto-collapse sidebar on tablet screens (768px - 1023px)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
    const handleResize = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        setIsSidebarCollapsed(true);
      }
    };
    // Initial check
    handleResize(mediaQuery);
    // Listen for changes
    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, []);
  // Track if this is the first render to skip the entry animation
  const isFirstRender = useRef(true);
  const skipInitial = isFirstRender.current;
  if (isFirstRender.current) {
    isFirstRender.current = false;
  }
  const activeAccountId = accounts?.activeAccountId || '';
  const activeAccount = accounts ? accounts.accountsById[activeAccountId] : null;
  const direction = useDirectionalTransition(currentView);
  const {
    isReducedMotion
  } = useMotionBudget();
  const transitionVariants = getTransitionVariants(direction, isReducedMotion);
  return <div className="min-h-screen bg-[#FAFAF8] flex flex-col font-sans text-slate-900" data-id="element-1538" data-mp="14cb6nf8i4ejk">
      {/* Storage Warning Banner (RES-4) */}
      <StorageWarningBanner data-id="element-1539" data-mp="199jp7gxkaklc" />

      <div className="flex-1 flex overflow-hidden" data-id="element-1540" data-mp="19cz5uo6vw1ov">
        {/* Skip-to-content link for keyboard accessibility */}
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-orange-500 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-300 font-semibold text-sm" data-id="element-1541" data-mp="1ibklaah270lf">
          
          Ga naar inhoud
        </a>

        {/* Desktop Sidebar */}
        <Sidebar currentView={currentView} onNavigate={onNavigate} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-1542" data-mp="3ckpw5mykkify" />
        

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden relative" data-id="element-1543" data-mp="32r8yu8z84l7j">
          {/* Mobile Header */}
          <header className="md:hidden h-20 bg-white/80 backdrop-blur-md border-b border-slate-200/60 flex items-center justify-between px-5 sticky top-0 z-30 shadow-sm" data-id="element-1544" data-mp="3vi0pys1fmjo0">
            <div className="flex items-center gap-3" data-id="element-1545" data-mp="3qh38yis5rvo3">
              <button onClick={() => setIsMobileNavOpen(true)} className="p-3 -ml-2 text-slate-600 hover:bg-orange-50 hover:text-orange-600 rounded-xl transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-orange-300" aria-label="Menu openen" data-id="element-1546" data-mp="3lj1t06q14cgu">
                
                <Menu className="w-6 h-6" data-id="element-1547" data-mp="2621bqwiyi41i" />
              </button>
              <span className="font-black text-slate-800 text-lg tracking-tight" data-id="element-1548" data-mp="3tzr3vsnf9mkd">
                {APP_NAME}
              </span>
            </div>

            <div className="flex items-center gap-3" data-id="element-1549" data-mp="2nfweym2gf84">
              {/* Mobile Profile Button. De accountwisselaar die hier stond was
                   demo-only: hij liet je zonder wachtwoord tussen negen
                   verzonnen accounts springen. */}
              {activeAccount && onLogout && <ProfileButton account={activeAccount} onNavigate={onNavigate} onLogout={onLogout} data-id="element-1550" data-mp="glxikrwbpjnx" />}
            </div>
          </header>

          {/* Desktop Header */}
          <header className="hidden md:flex h-20 bg-white/80 backdrop-blur-md border-b border-slate-200/60 items-center justify-end px-8 sticky top-0 z-30 shadow-sm" data-id="element-1551" data-mp="7jehac86rf3s">
            {activeAccount && onLogout && <ProfileButton account={activeAccount} onNavigate={onNavigate} onLogout={onLogout} data-id="element-1552" data-mp="2s9lv3tmxrt83" />}
          </header>

          {/* Scrollable Content */}
          <main id="main-content" className="flex-1 overflow-y-auto overflow-x-hidden p-6 md:p-10 scroll-smooth" data-id="element-1553" data-mp="2osr6wr5kn6dh">
            
            <div className="max-w-6xl mx-auto w-full pb-24 md:pb-12" data-id="element-1554" data-mp="2jufvwd3jipto">
              <AnimatePresence mode="wait" data-id="element-1555" data-mp="2yqx5ozw2zwe4">
                <motion.div key={currentView} initial={skipInitial ? false : transitionVariants.initial} animate={transitionVariants.animate} exit={transitionVariants.exit} transition={{
                duration: 0.25,
                ease: [0.25, 0.1, 0.25, 1] // Custom cubic-bezier for smoother feel
              }} className="w-full" data-id="element-1556" data-mp="2ypvrimjrqts6">
                  
                  {children}
                </motion.div>
              </AnimatePresence>
            </div>
          </main>
        </div>

        {/* Mobile Navigation Drawer */}
        <MobileNav isOpen={isMobileNavOpen} onClose={() => setIsMobileNavOpen(false)} currentView={currentView} onNavigate={onNavigate} onLogout={onLogout} data-id="element-1557" data-mp="2az9nd6cvxtng" />
        
      </div>

      {/* Floating Djay Buddy */}
      <FloatingDjay currentView={currentView} onNavigate={onNavigate} data-id="element-1558" data-mp="280sxn5hsxs24" />

      {/* Global Achievement Ceremony */}
      <AchievementCeremony onNavigateToCollection={() => onNavigate('collection')} data-id="element-1559" data-mp="2c265fl4gg5bo" />
      

      {/* Global cinematic skin-evolution reveal — fired by in-session unlocks
               AND the standalone DJ Goud long-press easter egg. */}
      <SkinEvolutionReveal data-id="element-1560" data-mp="2lqwgrnldx5nc" />

      {/* F5.6 — Offline Indicator */}
      <ConnectionStatus data-id="element-1561" data-mp="1p2gqfpagas96" />

      {/* PWA Install Prompt — verschijnt wanneer Chrome/Edge installatie aanbiedt */}
      <InstallAppPrompt data-id="element-1562" data-mp="1t4lf09m1kyoy" />
    </div>;
}